import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Collapse, DatePicker } from 'antd';
import { useTheme } from 'styled-components';
import moment, { Moment } from 'moment';
import { InsightReport } from './InsightReport';
import { Api } from '../../../Api/Api';
import { Button } from '../../../../components/Button/Button';

/**
 * Re-export of Ant Design Collapse.Panel for convenience
 */
export const Panel = Collapse.Panel;


export interface IOrchestrationQueryStatus {
	TotalFields?:      number;
	EligibleFields?:   number;
	ProcessedFields?:  number;
	TotalGrowers?:     number;
	EligibleGrowers?:  number;
	ProcessedGrowers?: number;
	TotalSellers?:     number;
	EligibleSellers?:  number;
	ProcessedSellers?: number;
	Status:           'Unknown' | 'NotStarted' | 'InProgress' | 'Completed' | 'Failed';
}

export type AnalysisSubject = 'Field' | 'Grower' | 'Seller' | 'Territory';

export type AnalysisPersona = 'Seller' | 'Grower' | 'SalesRep' | 'Agronomist' | 'DistrictManager';

/**
 * Props for the InsightsModal component
 */
interface IInsightsModalProps
{
	/** Controls modal visibility */
	isOpen: boolean;
	/** Callback invoked when modal is closed */
	onClose: () => void;

	persona: AnalysisPersona;
	subject: AnalysisSubject;

	/** ID of the entity being analyzed */
	entityId: string;
	entityName: string | undefined;

	/** Authentication token for API requests */
	authToken: string;
}

/**
 * Modal component for requesting and displaying AI-powered field insights.
 * Supports side-by-side comparison of results from two different AI models.
 * Double-click the "Field Insights" header to reveal/edit the system prompt.
 * 
 * @param props - Component props
 */
export const InsightsModal = (props: IInsightsModalProps) =>
{
	const { isOpen, onClose, entityId, authToken, persona, subject, entityName } = props;
	const api = useMemo(() => new Api('api/7', authToken, undefined), [authToken]);
	const theme = useTheme();

	const [showPrompt, setShowPrompt] = useState(false);
	// Default system prompt guides AI behavior and response format
	const [promptInput, setPromptInput] = useState<string>(undefined);
	// Analysis date defaults to today, restricted to current year
	const [snapshotDate, setSnapshotDate] = useState<Moment>(moment());

	useEffect(() => 
	{
		if (!isOpen)
		{
			// Don't call the apis if the modal is not open
			return;
		}
		setPromptInput(undefined);
		async function execute() 
		{
			const response = await api.getAsync<string>(`/ai/subjects/${subject}/personas/${persona}/prompt`);
			setPromptInput(response.Data);
		}
		execute();
	}, [subject, persona, api, isOpen]);

	const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
	const [currentOrchestrationStatus, setCurrentOrchestrationStatus] = useState<IOrchestrationQueryStatus>(null);

	const poll = useRef<NodeJS.Timeout>();
	const pollForOrchestrationStatus = useCallback((snapshotDate: Moment, subject: AnalysisSubject, entityId: string) => 
	{
		if (!isOpen)
		{
			// Don't call the apis if the modal is not open
			return;
		}
		setIsAnalyzing(true);
		async function execute()
		{
			const response = await api.postAsync<IOrchestrationQueryStatus>('/ai/orchestration/query', 
				{
					SnapshotDate: snapshotDate.format('YYYY-MM-DD'),
					SellerIdFilter: subject === 'Seller' ? entityId : undefined,
					GrowerIdFilter: subject === 'Grower' ?  entityId : undefined,
				});

			setCurrentOrchestrationStatus(response.Data);

			if(response.Data?.Status !== 'InProgress' && response.Data?.Status !== 'NotStarted')
			{
				setIsAnalyzing(false);
			}
			else
			{
				if(poll.current)
				{
					clearTimeout(poll.current);
				}
				poll.current = setTimeout(() => pollForOrchestrationStatus(snapshotDate, subject, entityId), 5000);
			}
		}
		execute();
	}, [api, isOpen]);

	useEffect(() => 
	{
		if (!isOpen)
		{
			// Don't call the apis if the modal is not open
			return;
		}
		setCurrentOrchestrationStatus(null);
		pollForOrchestrationStatus(snapshotDate, subject, entityId);
	}, [snapshotDate, subject, entityId,isOpen, pollForOrchestrationStatus]);

	const triggerFullAnalysis = useCallback(() => 
	{
		async function execute()
		{
			setIsAnalyzing(true);
			const response = await api.postAsync<string>('/ai/orchestration', {
				ConcurrencyLimit: 2,
				UpdateWindow: 6,
				SnapshotDate: snapshotDate.format('YYYY-MM-DD'),
				SellerIdFilter: subject === 'Seller' ? entityId : undefined,
				GrowerIdFilter: subject === 'Grower' ?  entityId : undefined,
				Provider: 'Anthropic',
				Model: 'claude-haiku-4-5-20251001',
				Reason: 'Full Analysis from Troubleshooting Tool'
			});

			pollForOrchestrationStatus(snapshotDate, subject, entityId);
		}
		execute();
	}, [snapshotDate, subject, entityId, api]);

	const analysisButton = useMemo(() => 
	{
		// A workflow is already in progress
		if(currentOrchestrationStatus?.Status === 'InProgress' || currentOrchestrationStatus?.Status === 'NotStarted')
		{
			let target = 'fields';
			let progress = currentOrchestrationStatus?.ProcessedFields;
			let total = currentOrchestrationStatus?.EligibleFields;

			if(currentOrchestrationStatus?.EligibleGrowers > 0 && currentOrchestrationStatus?.ProcessedFields ===  currentOrchestrationStatus?.EligibleFields)
			{
				target = 'growers';
				progress = currentOrchestrationStatus?.ProcessedGrowers;
				total = currentOrchestrationStatus?.EligibleGrowers;
			}

			if(currentOrchestrationStatus?.EligibleSellers > 0 && currentOrchestrationStatus?.ProcessedGrowers ===  currentOrchestrationStatus?.EligibleGrowers)
			{
				target = 'sellers';
				progress = currentOrchestrationStatus?.ProcessedSellers;
				total = currentOrchestrationStatus?.EligibleSellers;
			}

			return <Button variant='reversed' disabled={true}>In Progress {progress} of {total} {target}...</Button>;
		}

		// We launched a workflow but don't have a status yet
		if(isAnalyzing)
		{
			return <Button variant='reversed' disabled={true}>Analyzing...</Button>;
		}

		return <Button variant='reversed' onClick={triggerFullAnalysis}>
			Full Analysis (VERY slow){currentOrchestrationStatus?.TotalFields > 0 ? ` of ${currentOrchestrationStatus.TotalFields} fields.` : ''}
		</Button>;
	}, [currentOrchestrationStatus, isAnalyzing]);
	
	return (
		<Modal
			style={{ 
				top: 20, 
				left: 20,
				right: 20, 
				bottom: 20
			}}
			bodyStyle={{ height: '85vh', padding: '20px' }}
			width={'90%'}
			open={isOpen}
			onOk={onClose}
			onCancel={onClose}
			footer={null}
		>
			<div style={{
				width: '100%', height: '100%', overflowY: 'auto'
			}}>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					{/* Double-click header to show/edit the system prompt */}
					<h2 style={{ marginBottom: '20px' }} onDoubleClick={() => setShowPrompt(true)}>
						{subject} Root Cause {entityName && `for ${entityName}`}
					</h2>
					<div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<label style={{ 
								fontWeight: theme.fontWeights.bold,
								fontSize: theme.fontSizes.normal
							}}>
								Analysis Date:
							</label>
							<DatePicker
								value={snapshotDate}
								onChange={(date) => setSnapshotDate(date || moment())}
								format="YYYY-MM-DD"
								disabledDate={(current) => 
								{
									const currentYear = moment().year();
									const startOfYear = moment().startOf('year');
									const today = moment().endOf('day');
									return current && (
										current.year() !== currentYear ||
										current.isBefore(startOfYear) ||
										current.isAfter(today)
									);
								}}
							/>
						</div>
						{
							(subject === 'Grower' || subject === 'Seller') && analysisButton
						}
					</div>
					{
						showPrompt && <div style={{ marginBottom: '10px' }}>
							<label style={{ 
								display: 'block', 
								marginBottom: '8px',
								fontWeight: theme.fontWeights.bold,
								fontSize: theme.fontSizes.normal
							}}>
								Enter your prompt:
							</label>
							<Input.TextArea
								value={promptInput}
								disabled={promptInput === undefined}
								onChange={(e) => setPromptInput(e.target.value)}
								placeholder="As an agronomist..."
								rows={20}
								style={{ marginBottom: '10px' }}
							/>
						</div>
					}
					{/* Two InsightReports for side-by-side model comparison */}
					<div style={{ display: 'flex', flexDirection: 'row', gap: '20px', maxWidth: '100%', overflowX: 'auto' }}>
						<InsightReport prompt={promptInput} entityId={entityId} authToken={authToken} subject={subject} persona={persona} snapshotDate={snapshotDate.format('YYYY-MM-DD')} />
						<InsightReport prompt={promptInput} entityId={entityId} authToken={authToken} subject={subject} persona={persona} snapshotDate={snapshotDate.format('YYYY-MM-DD')} />
					</div>
				</div>
			</div>
		</Modal>
	);
};
